import { useState, useCallback, useRef, useEffect } from 'react';
/**
 * Width-only resizable dialog support. Renders no DOM itself — the caller wires
 * `startLeftResize` / `startRightResize` to mousedown handlers on bottom-corner
 * grip elements.
 *
 * Right-side resize: width grows/shrinks, left position unchanged.
 * Left-side resize: width grows/shrinks, left position adjusts so the right edge stays put.
 */
export function useDialogResize({ initialWidth, minWidth = 320, maxWidth = Math.floor(typeof window !== 'undefined' ? window.innerWidth * 0.95 : 1200), position, setPosition, }) {
    const [width, setWidth] = useState(initialWidth);
    const [isResizing, setIsResizing] = useState(false);
    // Refs so the mousemove handler always reads the latest state without re-binding.
    const widthRef = useRef(width);
    const positionRef = useRef(position);
    const setPositionRef = useRef(setPosition);
    useEffect(() => { widthRef.current = width; }, [width]);
    useEffect(() => { positionRef.current = position; }, [position]);
    useEffect(() => { setPositionRef.current = setPosition; }, [setPosition]);
    const startRightResize = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = widthRef.current;
        setIsResizing(true);
        const onMove = (m) => {
            const dx = m.clientX - startX;
            const newW = Math.max(minWidth, Math.min(maxWidth, startWidth + dx));
            setWidth(newW);
        };
        const onUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [minWidth, maxWidth]);
    const startLeftResize = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = widthRef.current;
        const startLeft = positionRef.current.x;
        setIsResizing(true);
        const onMove = (m) => {
            const dx = m.clientX - startX;
            const newW = Math.max(minWidth, Math.min(maxWidth, startWidth - dx));
            const actualDx = startWidth - newW; // how much the left edge actually moved
            setWidth(newW);
            setPositionRef.current({ x: startLeft + actualDx, y: positionRef.current.y });
        };
        const onUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [minWidth, maxWidth]);
    return { width, isResizing, startLeftResize, startRightResize };
}
